import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import apiClient from '../../../server/server';

// Get base URL from the apiClient configuration
const baseURL = apiClient.defaults.baseURL;

export const businessRequestsApiSlice = createApi({
  reducerPath: 'businessRequestsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: baseURL,
    prepareHeaders: (headers, { getState }) => {
      // Add auth token if available
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['BusinessProfileRequest'],
  endpoints: (builder) => ({
    // Get all business profile requests (Admin)
    getAllBusinessProfileRequests: builder.query({
      query: ({ status, requestType, page = 1, limit = 10, search } = {}) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (requestType) params.append('requestType', requestType);
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        if (search) params.append('search', search);
        
        return `/business-profile-requests?${params.toString()}`;
      },
      providesTags: ['BusinessProfileRequest'],
    }),

    // Get user's own requests
    getUserBusinessProfileRequests: builder.query({
      query: () => '/business-profile-requests/my-requests',
      providesTags: ['BusinessProfileRequest'],
    }),

    // Get single request by ID
    getBusinessProfileRequestById: builder.query({
      query: (id) => `/business-profile-requests/${id}`,
      providesTags: (result, error, id) => [{ type: 'BusinessProfileRequest', id }],
    }),

    // Get statistics (Admin)
    getBusinessProfileRequestStats: builder.query({
      query: () => '/business-profile-requests/stats',
    }),

    // Submit new business profile request
    submitBusinessProfileRequest: builder.mutation({
      query: (requestData) => ({
        url: '/business-profile-requests',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['BusinessProfileRequest'],
    }),

    // Review request (Admin: Approve/Reject)
    reviewBusinessProfileRequest: builder.mutation({
      query: ({ id, status, reviewNotes, createdBusinessProfile, executeClaim }) => ({
        url: `/business-profile-requests/${id}/review`,
        method: 'PUT',
        body: { status, reviewNotes, createdBusinessProfile, executeClaim },
      }),
      invalidatesTags: ['BusinessProfileRequest'],
    }),

    // Delete request
    deleteBusinessProfileRequest: builder.mutation({
      query: (id) => ({
        url: `/business-profile-requests/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['BusinessProfileRequest'],
    }),

    // Assign profile ownership (Admin)
    assignProfileOwnership: builder.mutation({
      query: ({ profileId, userId, setAsDefault = true, businessProfileRequestId }) => ({
        url: '/business-profile-requests/assign-profile-ownership',
        method: 'POST',
        body: { profileId, userId, setAsDefault, businessProfileRequestId },
      }),
      invalidatesTags: ['BusinessProfileRequest'],
    }),
  }),
});

export const {
  useGetAllBusinessProfileRequestsQuery,
  useGetUserBusinessProfileRequestsQuery,
  useGetBusinessProfileRequestByIdQuery,
  useGetBusinessProfileRequestStatsQuery,
  useSubmitBusinessProfileRequestMutation,
  useReviewBusinessProfileRequestMutation,
  useDeleteBusinessProfileRequestMutation,
  useAssignProfileOwnershipMutation,
} = businessRequestsApiSlice;